import type { I18n, VueI18n } from 'vue-i18n'
import { bsConstants } from 'booksprout'

import { getRequestContext } from '../../base/store/storeContext'
import { resolveLocale } from '../../boot/i18n'
import { useCookies } from '../cookies/cookiesModule'

/**
 * Replaces the `TranslationService` DI token, which was the one binding registered with
 * `container.bind(...).toConstantValue(i18n)` rather than as a class.
 *
 * ApiErrorHandler and everything derived from it needs to translate error codes from outside a
 * component, which is why the i18n instance was in the container at all.
 *
 * The instance is request-scoped on the server, exactly as the cookie jar is. It used to be a
 * module-scope singleton, which was harmless while every app rendered in en-US — every instance
 * held the same messages. It stopped being harmless when the reader gained a language picker: two
 * requests rendered at the same moment can now be in two different locales, and the SSR bundle
 * runs with `runInNewContext: false`, so one slot would mean one reader switching to Spanish
 * could render Spanish into someone else's page mid-request. [sc-27608]
 *
 * What is held is the APP-LEVEL instance — the thing `app.use()` takes. vue-i18n 9 splits what v8
 * had in one object: `createI18n()` returns that, and `.global` is the VueI18n the components
 * actually see. Both are needed (the boot file installs the plugin, everything else translates),
 * so the split is absorbed here rather than at the four call sites.
 */

/** Browser: one instance for the page's lifetime, which is what the four apps had. */
let clientI18n: I18n | undefined

const useI18n = () => {
  /**
   * Deliberately tolerant of there being no request context.
   *
   * `getRequestContext` throws when state is touched on the server outside a request, which is
   * the right behaviour for state — silently sharing it between users is worse than a crash. A
   * translator is different: `t`/`tc` are reached from error handling, which runs on paths that
   * can fire before boot completes and in specs that never boot at all, and a missing
   * translation must never be the reason a request fails. So absence is answered with the key
   * below rather than an exception.
   */
  const current = (): I18n | undefined => {
    if (process.env.SERVER) {
      try {
        return getRequestContext().i18n as I18n | undefined
      } catch {
        return void 0
      }
    }

    return clientI18n
  }

  const init = (i18n: I18n) => {
    if (process.env.SERVER) {
      getRequestContext().i18n = i18n
      return
    }

    clientI18n = i18n
  }

  /**
   * The translator: what every caller means by "the i18n instance", and what `applyLocale` and the
   * language picker's `$i18n` both operate on.
   *
   * The cast is because `.global` is `Composer | VueI18n` until the `legacy` flag is resolved
   * statically, and ours is set in an options object built inside `createI18n` (see boot/i18n.ts).
   */
  const i18n = (): VueI18n => current()?.global as VueI18n

  /** The app-level instance, for the one caller that needs it: `app.use()` in each i18n boot file. */
  const plugin = (): I18n => current() as I18n

  /**
   * Which locale this request should open in: the cookie the reader's last choice was written to, or
   * else the default. The user's stored preference is applied later, by the language picker, once the
   * user has actually loaded.
   *
   * NOT Accept-Language, although the server could read it. The browser cannot, and the page renders
   * twice — on the server, then in the browser to hydrate — so the two have to agree: a first visit from
   * a German browser got a German "Loading ..." from the server and an English one from the client, and
   * Vue reported the hydration mismatch on every such page (sc-28743). `navigator.language` is not
   * consulted either, so that a reader who has chosen a language keeps it.
   *
   * Unlike `t`/`tc` this does NOT tolerate being called on the server outside a request — the
   * cookie jar it reads comes from the request too, and `cookiesModule` throws there on purpose.
   * Resolving a locale is a boot-time question that only has an answer within a request, so the
   * loud failure is right.
   */
  const initialLocale = (): string => resolveLocale({
    cookieLocale: useCookies().get(bsConstants.I18N.COOKIE_NAME)
  })

  /**
   * `tc` guarded against the instance not being initialised. Error handling runs on paths that
   * can fire before boot completes (and in specs that never boot at all), and a missing
   * translation must never be the reason a request fails — so fall back to the key, which is
   * also what vue-i18n does for an unknown key.
   */
  /**
   * Both are called with whatever vue-i18n accepts -- a key, a key and a count, a key and a named
   * argument object. v9 states that as a dozen overloads, and a spread of `any[]` matches none of
   * them ("a spread argument must either have a tuple type"), so the call goes through one narrow
   * alias rather than a cast at each site.
   */
  type Translate = (key: string, ...args: any[]) => string

  const tc = (key: string, ...args: any[]): string => {
    const instance = i18n()

    if (!instance) {
      return key
    }

    return (instance.tc as Translate)(key, ...args)
  }

  const t = (key: string, ...args: any[]): string => {
    const instance = i18n()

    if (!instance) {
      return key
    }

    return (instance.t as Translate)(key, ...args)
  }

  /**
   * `te`, for copy that is optional: the discount tables ask whether a plan has a per-plan blurb
   * before rendering one. Falls back to `false` rather than the key -- a missing instance means no
   * messages are loaded, which is exactly "this key does not exist".
   */
  const te = (key: string): boolean => {
    const instance = i18n()

    if (!instance) {
      return false
    }

    return instance.te(key)
  }

  const reset = () => {
    if (process.env.SERVER) {
      try {
        getRequestContext().i18n = void 0
      } catch {
        // No request to clear.
      }

      return
    }

    clientI18n = void 0
  }

  return {
    init,
    i18n,
    plugin,
    initialLocale,
    t,
    tc,
    te,
    reset
  }
}

export { useI18n }
